import React from 'react'
import {
  isConfiguredForConditionalPrefillEdit,
  isPrefillEditFieldRequired
} from './prefillPreviewUtils'
import { resolvePrefillEditFieldId } from '../../../../components/formBuilder/editForm/tableFieldUtils'

interface Props {
  element: any
  formErrors?: Record<string, string>
  onEditFieldChange: (
    parentFieldId: number,
    editFieldId: number,
    value: string,
    editFieldIndex?: number
  ) => void
}

const getHtmlInputType = (type: string): string => {
  switch (type) {
    case 'number':
      return 'number'
    case 'email':
      return 'email'
    case 'tel':
      return 'tel'
    case 'url':
      return 'url'
    case 'password':
      return 'password'
    case 'date':
      return 'date'
    case 'time':
      return 'time'
    case 'datetime':
      return 'datetime-local'
    default:
      return 'text'
  }
}

const PreviewPrefillConditionalEditSection: React.FC<Props> = ({
  element,
  formErrors = {},
  onEditFieldChange
}) => {
  if (
    !isConfiguredForConditionalPrefillEdit(element) ||
    !element.prefillConditionalEditActive ||
    !Array.isArray(element.prefillEditFields) ||
    element.prefillEditFields.length === 0
  ) {
    return null
  }

  const renderEditFieldInput = (editField: any, editFieldIndex: number) => {
    const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex, element.fieldId)

    if (editField.type === 'textarea') {
      return (
        <textarea
          className='readonly-form-preview__input'
          rows={3}
          placeholder={editField.placeholder || ''}
          value={editField.value || ''}
          onChange={(e) =>
            onEditFieldChange(element.fieldId, editFieldId, e.target.value, editFieldIndex)
          }
        />
      )
    }

    if (editField.type === 'select') {
      return (
        <select
          className='readonly-form-preview__input'
          value={editField.value || ''}
          onChange={(e) =>
            onEditFieldChange(element.fieldId, editFieldId, e.target.value, editFieldIndex)
          }
        >
          <option value=''>Select...</option>
          {(editField.options || []).map((option: any) => (
            <option key={option.value || option.text} value={option.text || option.value}>
              {option.text || option.value}
            </option>
          ))}
        </select>
      )
    }

    if (editField.type === 'file') {
      return (
        <input
          type='file'
          className='readonly-form-preview__input'
          accept={editField.accept}
          onChange={(e) => {
            const file = e.target.files?.[0]
            onEditFieldChange(element.fieldId, editFieldId, file ? file.name : '', editFieldIndex)
          }}
        />
      )
    }

    return (
      <input
        id={`preview-prefill-edit-${element.fieldId}-${editFieldId}-${editFieldIndex}`}
        name={`preview-prefill-edit-${element.fieldId}-${editFieldId}-${editFieldIndex}`}
        type={getHtmlInputType(editField.type)}
        className='readonly-form-preview__input'
        placeholder={editField.placeholder || ''}
        value={editField.value || ''}
        autoComplete='off'
        onChange={(e) =>
          onEditFieldChange(element.fieldId, editFieldId, e.target.value, editFieldIndex)
        }
      />
    )
  }

  return (
    <div className='mt-3 p-3 border border-warning rounded bg-warning bg-opacity-10 prefill-conditional-edit-section'>
      <div className='d-flex align-items-center gap-2 mb-3'>
        <span className='badge bg-warning text-dark'>
          {element.prefillEditFields.some((editField: any) => isPrefillEditFieldRequired(editField))
            ? 'Additional fields required'
            : 'Additional fields'}
        </span>
        <small className='text-muted'>
          {element.prefillEditFields.some((editField: any) => isPrefillEditFieldRequired(editField))
            ? 'Complete all required fields below to submit the form'
            : 'Optional fields below'}
        </small>
      </div>
      {element.prefillEditFields.map((editField: any, editFieldIndex: number) => {
        const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex, element.fieldId)
        const fieldError = formErrors[`edit_${element.fieldId}_${editFieldId}`]
        return (
          <div key={`${editFieldId}-${editFieldIndex}`} className='mb-3'>
            <label className='readonly-form-preview__label'>
              {editField.label}
              {isPrefillEditFieldRequired(editField) ? (
                <span className='text-danger ms-1' aria-hidden='true'>
                  *
                </span>
              ) : null}
            </label>
            {renderEditFieldInput(editField, editFieldIndex)}
            {fieldError ? <small className='text-danger d-block mt-1'>{fieldError}</small> : null}
          </div>
        )
      })}
    </div>
  )
}

export default PreviewPrefillConditionalEditSection
